/**
 * DriverProfilePage — `/driver-portal/profile`.
 *
 * Live · GET /v1/driver-portal/me + PATCH /v1/driver-portal/me/profile.
 * Personal / Emergency / Banking / Vehicle tabs are editable. KYC and
 * Security tabs read from the citizen-scoped cross-service surfaces:
 *
 *   - KYC: GET /v1/customers/me/kyc (service-customer) — masked NIN/BVN +
 *     kycLevel projection. The `documents` / `sanctionsScreened` /
 *     `kycVerifiedAt` placeholders are surfaced honestly as inline notes
 *     because the citizen-scoped doc / watchlist / verification ledger
 *     surfaces are not exposed yet.
 *   - Security: GET /v1/auth/me/security (service-identity) — verification
 *     flags (derived from User.status), last-login timestamp, and the active
 *     session count. "Sign out everywhere" calls
 *     POST /v1/auth/me/sign-out-everywhere; "Change password" routes to the
 *     existing forgot-password flow (the driver portal does not own a
 *     dedicated change-password page yet).
 */
import { type FormEvent, type JSX, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Building2,
  KeyRound,
  LogOut,
  ShieldCheck,
  Truck,
  UserCircle2,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { type CitizenMeKycResponse, getMyKyc } from '../../lib/api/customers';
import {
  type FullDriverProfile,
  type UpdateProfileRequest,
  getDriverPortalMe,
  updateDriverProfile,
} from '../../lib/api/driver-portal';
import {
  type MySecurityResponse,
  getMySecurity,
  signOutEverywhere,
} from '../../lib/api/identity-admin';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { useDriverPortalContext } from './DriverPortalLayout';

type Tab = 'personal' | 'emergency' | 'banking' | 'kyc' | 'vehicle' | 'security';

export function DriverProfilePage(): JSX.Element {
  const { driverId, displayName, initials } = useDriverPortalContext();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('personal');

  const driver = useQuery({
    queryKey: ['driver-portal', 'me', driverId],
    queryFn: () => getDriverPortalMe(driverId) as Promise<FullDriverProfile>,
    retry: false,
    staleTime: 60_000,
  });

  const denied =
    driver.isError &&
    driver.error instanceof ApiError &&
    (driver.error.status === 401 || driver.error.status === 403);
  const notFound =
    driver.isError &&
    driver.error instanceof ApiError &&
    driver.error.status === 404;
  const data = driver.data ?? null;

  const tabs: readonly { key: Tab; label: string; icon: typeof UserCircle2 }[] = [
    { key: 'personal', label: 'Personal', icon: UserCircle2 },
    { key: 'emergency', label: 'Emergency', icon: AlertCircle },
    { key: 'banking', label: 'Banking', icon: Building2 },
    { key: 'kyc', label: 'KYC', icon: ShieldCheck },
    { key: 'vehicle', label: 'Vehicle', icon: Truck },
    { key: 'security', label: 'Security', icon: KeyRound },
  ];

  return (
    <div>
      <PageHeader
        title="Profile & banking"
        description="Your driver profile (banking edits have a 24-hour cooling-off period)."
      />

      <div className="bg-white border border-border rounded-xl overflow-hidden mb-5">
        <div className="p-5 flex items-center gap-4 border-b border-border bg-gray-50">
          <div className="w-16 h-16 rounded-full bg-nipost-green flex items-center justify-center text-white font-bold text-xl">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-gray-900">{data?.fullName ?? displayName}</p>
            <p className="text-xs text-muted">
              {data?.driverNumber ?? 'Driver'}
              {data ? ` · ${data.primaryPhone}` : ''}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]">
              <span className="bg-white border border-border px-2 py-0.5 rounded font-mono">{driverId}</span>
              {data ? <StatusBadge status={data.status} /> : null}
            </div>
          </div>
        </div>

        <div className="border-b border-border px-2 overflow-x-auto">
          <nav className="flex" role="tablist" aria-label="Profile tabs">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    active ? 'border-nipost-green text-nipost-green' : 'border-transparent text-muted hover:text-gray-900'
                  }`}
                  data-testid={`tab-${t.key}`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-5">
          {driver.isPending ? (
            <p className="text-sm text-muted">Loading profile…</p>
          ) : denied ? (
            <DeferredNotice title="Permission required">
              The dev token doesn&apos;t carry <code className="font-mono">Driver/readSelf</code>.
            </DeferredNotice>
          ) : notFound ? (
            <DeferredNotice title="Driver record not found">
              Your account <code className="font-mono">{driverId}</code> isn&apos;t linked to a driver profile yet.
              Ask your dispatcher to provision one for you.
            </DeferredNotice>
          ) : driver.isError ? (
            <DeferredNotice title="Service unreachable">{(driver.error as Error).message}</DeferredNotice>
          ) : (
            <ProfileTab
              tab={tab}
              data={data}
              driverId={driverId}
              onSaved={() => void qc.invalidateQueries({ queryKey: ['driver-portal', 'me', driverId] })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileTab(props: {
  readonly tab: Tab;
  readonly data: FullDriverProfile | null;
  readonly driverId: string;
  readonly onSaved: () => void;
}): JSX.Element {
  const { tab, data, driverId, onSaved } = props;

  if (tab === 'personal') {
    return <PersonalTab data={data} driverId={driverId} onSaved={onSaved} />;
  }
  if (tab === 'emergency') {
    return <EmergencyTab data={data} driverId={driverId} onSaved={onSaved} />;
  }
  if (tab === 'banking') {
    return <BankingTab data={data} driverId={driverId} onSaved={onSaved} />;
  }
  if (tab === 'vehicle') {
    return (
      <div className="space-y-3">
        <Field label="Assigned vehicle" value={data?.assignedVehicleId ?? '—'} note="Managed by Fleet Operations." />
        <p className="text-xs text-muted bg-gray-50 rounded-lg p-3">
          Vehicle assignment is owned by Fleet Operations. Daily inspection is part of clock-in.
        </p>
      </div>
    );
  }
  if (tab === 'kyc') {
    return <KycTab />;
  }
  return <SecurityTab />;
}

// ---------------------------------------------------------------------------
// KYC tab — pulls /v1/customers/me/kyc (service-customer, Customer/read-self).
// ---------------------------------------------------------------------------

function KycTab(): JSX.Element {
  const kyc = useQuery({
    queryKey: ['driver-portal', 'kyc'],
    queryFn: () => getMyKyc(),
    retry: false,
    staleTime: 60_000,
  });

  if (kyc.isPending) {
    return <p className="text-sm text-muted">Loading KYC profile…</p>;
  }
  const denied =
    kyc.isError && kyc.error instanceof ApiError && (kyc.error.status === 401 || kyc.error.status === 403);
  const notFound =
    kyc.isError && kyc.error instanceof ApiError && kyc.error.status === 404;
  if (denied) {
    return (
      <DeferredNotice title="Permission required">
        Your token doesn&apos;t carry <code className="font-mono">Customer/read-self</code>.
        Re-login with a citizen-scoped JWT.
      </DeferredNotice>
    );
  }
  if (notFound) {
    return (
      <DeferredNotice title="Customer record missing">
        No customer profile is linked to your account. Ask your dispatcher to provision one.
      </DeferredNotice>
    );
  }
  if (kyc.isError) {
    return <DeferredNotice title="Service unreachable">{(kyc.error as Error).message}</DeferredNotice>;
  }

  const data: CitizenMeKycResponse = kyc.data;
  return (
    <div className="space-y-3 max-w-lg">
      <Field label="Customer number" value={data.customerNumber} note="Read-only" />
      <Field label="Full name" value={data.fullName} note="Read-only — edit through HR" />
      <Field label="KYC tier" value={data.kycLevel} note={
        data.kycLevel === 'NONE' ? 'No verification on file' : 'Active'
      } />
      <Field
        label="NIN"
        value={data.ninMasked ?? 'Not captured'}
        note={data.ninMasked ? 'Last 4 digits shown' : 'Submit at any NIPOST outlet to upgrade your tier'}
      />
      <Field
        label="BVN"
        value={data.bvnMasked ?? 'Not captured'}
        note={data.bvnMasked ? 'Last 4 digits shown' : 'Optional — required for ENHANCED tier'}
      />

      <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 space-y-1">
        <p className="font-semibold">A few KYC surfaces aren&apos;t exposed citizen-side yet:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Verification timestamp (service-kyc owns the ledger; the citizen-scoped read surface is a follow-up slice).</li>
          <li>Sanctions / watchlist status (staff-scoped only today).</li>
          <li>Document attachment surface (passport / driver&apos;s licence / utility bill) not exposed yet.</li>
        </ul>
        <p>For tier upgrades or to upload documents, visit your nearest NIPOST outlet.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Security tab — pulls /v1/auth/me/security (service-identity).
// ---------------------------------------------------------------------------

function SecurityTab(): JSX.Element {
  const qc = useQueryClient();
  const security = useQuery({
    queryKey: ['driver-portal', 'security'],
    queryFn: () => getMySecurity(),
    retry: false,
    staleTime: 30_000,
  });

  const signOut = useMutation({
    mutationFn: () => signOutEverywhere(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['driver-portal', 'security'] });
    },
  });

  if (security.isPending) {
    return <p className="text-sm text-muted">Loading security profile…</p>;
  }
  if (security.isError) {
    if (security.error instanceof ApiError && (security.error.status === 401 || security.error.status === 403)) {
      return (
        <DeferredNotice title="Permission required">
          Re-login to view security settings.
        </DeferredNotice>
      );
    }
    return <DeferredNotice title="Service unreachable">{(security.error as Error).message}</DeferredNotice>;
  }

  const data: MySecurityResponse = security.data;
  return (
    <div className="space-y-3 max-w-lg">
      <Field
        label="Email"
        value={data.email}
        note={data.emailVerified ? 'Verified' : 'Not verified'}
      />
      <Field
        label="Mobile"
        value={data.phone ?? '—'}
        note={data.phone ? (data.phoneVerified ? 'Verified' : 'Not verified') : 'No phone on file'}
      />
      <Field
        label="Last login"
        value={data.lastLoginAt ? new Date(data.lastLoginAt).toLocaleString() : 'Never'}
        note="Stamped on every successful sign-in"
      />
      <Field
        label="Password last changed"
        value={data.passwordChangedAt ? new Date(data.passwordChangedAt).toLocaleString() : 'Never changed'}
      />
      <Field
        label="Active sessions"
        value={String(data.activeSessionCount)}
        note="Refresh tokens that have not been revoked or expired."
      />

      <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
        Verification flags are derived from your account status — separate email / phone
        verification ledgers are deferred to a later slice.
      </div>

      <div className="pt-3 flex flex-col sm:flex-row gap-2">
        <Link
          to="/auth/login?reset=1"
          className="px-4 py-2 bg-white border border-border text-sm font-semibold rounded-lg hover:bg-gray-50 text-center"
          data-testid="security-change-password"
          title="Use the password-reset flow on the sign-in page"
        >
          Change password
        </Link>
        <button
          type="button"
          onClick={() => signOut.mutate()}
          disabled={signOut.isPending || data.activeSessionCount === 0}
          data-testid="security-sign-out-everywhere"
          className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" />
          {signOut.isPending ? 'Signing out…' : 'Sign out everywhere'}
        </button>
      </div>
      {signOut.isSuccess ? (
        <p className="text-xs text-green-700">
          Revoked {signOut.data.revoked} active {signOut.data.revoked === 1 ? 'session' : 'sessions'}.
          Your current tab keeps working until its access token expires.
        </p>
      ) : null}
      {signOut.isError ? (
        <p className="text-xs text-red-700">{(signOut.error as Error).message}</p>
      ) : null}
    </div>
  );
}

function PersonalTab(props: { data: FullDriverProfile | null; driverId: string; onSaved: () => void }): JSX.Element {
  const { data, driverId, onSaved } = props;
  const [phone, setPhone] = useState<string>('');
  useEffect(() => setPhone(data?.primaryPhone ?? ''), [data?.primaryPhone]);

  const update = useMutation({
    mutationFn: (body: UpdateProfileRequest) => updateDriverProfile(body, driverId),
    onSuccess: () => onSaved(),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    update.mutate({ primaryPhone: phone });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 max-w-lg">
      <Field label="Full name" value={data?.fullName ?? '—'} note="Read-only — edit through HR" />
      <Field label="Driver number" value={data?.driverNumber ?? '—'} note="Read-only" />
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted font-semibold">Mobile</p>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg bg-white"
          data-testid="profile-phone"
        />
      </div>
      <Field label="Licence" value={data?.licenseNumber ?? '—'} note={
        data?.licenseExpiresAt ? `expires ${new Date(data.licenseExpiresAt).toLocaleDateString()}` : 'Read-only'
      } />
      <Field label="Hired" value={data?.hiredAt ? new Date(data.hiredAt).toLocaleDateString() : '—'} note="Read-only" />
      <button
        type="submit"
        disabled={update.isPending}
        data-testid="save-personal"
        className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
      >
        {update.isPending ? 'Saving…' : 'Save mobile'}
      </button>
      {update.isError ? <p className="text-xs text-red-700">{(update.error as Error).message}</p> : null}
    </form>
  );
}

function EmergencyTab(props: { data: FullDriverProfile | null; driverId: string; onSaved: () => void }): JSX.Element {
  const { data, driverId, onSaved } = props;
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [relation, setRelation] = useState<string>('');
  useEffect(() => {
    setName(data?.emergencyContactName ?? '');
    setPhone(data?.emergencyContactPhone ?? '');
    setRelation(data?.emergencyContactRelation ?? '');
  }, [data]);

  const update = useMutation({
    mutationFn: (body: UpdateProfileRequest) => updateDriverProfile(body, driverId),
    onSuccess: () => onSaved(),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    update.mutate({
      emergencyContactName: name,
      emergencyContactPhone: phone,
      emergencyContactRelation: relation,
    });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 max-w-lg">
      <FieldInput label="Contact name" value={name} onChange={setName} testid="emergency-name" />
      <FieldInput label="Contact phone" value={phone} onChange={setPhone} type="tel" testid="emergency-phone" />
      <FieldInput label="Relationship" value={relation} onChange={setRelation} testid="emergency-relation" />
      <button
        type="submit"
        disabled={update.isPending}
        data-testid="save-emergency"
        className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
      >
        {update.isPending ? 'Saving…' : 'Save emergency contact'}
      </button>
      {update.isError ? <p className="text-xs text-red-700">{(update.error as Error).message}</p> : null}
    </form>
  );
}

function BankingTab(props: { data: FullDriverProfile | null; driverId: string; onSaved: () => void }): JSX.Element {
  const { data, driverId, onSaved } = props;
  const [bankName, setBankName] = useState<string>('');
  const [acctNumber, setAcctNumber] = useState<string>('');
  const [acctName, setAcctName] = useState<string>('');
  useEffect(() => {
    setBankName(data?.bankName ?? '');
    setAcctNumber(data?.bankAccountNumber ?? '');
    setAcctName(data?.bankAccountName ?? '');
  }, [data]);

  const update = useMutation({
    mutationFn: (body: UpdateProfileRequest) => updateDriverProfile(body, driverId),
    onSuccess: () => onSaved(),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    update.mutate({
      bankName,
      bankAccountNumber: acctNumber,
      bankAccountName: acctName,
    });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 max-w-lg">
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
        Banking changes are subject to a 24-hour cooling-off period. Saved at:{' '}
        {data?.bankUpdatedAt ? new Date(data.bankUpdatedAt).toLocaleString() : '—'}.
      </p>
      <FieldInput label="Bank name" value={bankName} onChange={setBankName} testid="bank-name" />
      <FieldInput
        label="Account number"
        value={acctNumber}
        onChange={(v) => setAcctNumber(v.replace(/\D/g, ''))}
        testid="bank-account-number"
      />
      <FieldInput label="Account name" value={acctName} onChange={setAcctName} testid="bank-account-name" />
      <button
        type="submit"
        disabled={update.isPending}
        data-testid="save-banking"
        className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
      >
        {update.isPending ? 'Saving…' : 'Save banking'}
      </button>
      {update.isError ? <p className="text-xs text-red-700">{(update.error as Error).message}</p> : null}
    </form>
  );
}

function FieldInput(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
  type?: string;
}): JSX.Element {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-muted font-semibold">{props.label}</p>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg bg-white"
        data-testid={props.testid}
      />
    </div>
  );
}

function Field(props: { readonly label: string; readonly value: string; readonly note?: string }): JSX.Element {
  return (
    <div className="px-4 py-3 bg-gray-50 rounded-lg">
      <p className="text-[10px] uppercase tracking-widest text-muted font-semibold">{props.label}</p>
      <p className="text-sm font-medium text-gray-900 mt-0.5">{props.value}</p>
      {props.note ? <p className="text-[10px] text-muted mt-0.5">{props.note}</p> : null}
    </div>
  );
}
